import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useEffect, useMemo } from "react";
import { isDefaultCommandStep, Outcome, State } from "~/lib/pipeline";
import {
  filterSteps,
  FilterByOption,
  StateOrder,
} from "~/components/build/Show/lib/useFilterStore";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";

export function filterKey(filters: FilterByOption[]): string {
  return [...filters].sort().join("|");
}

export default function useExpandedIds(filters: FilterByOption[] = []) {
  const { build } = useBuild();
  const { steps } = useSteps();
  const expandedStateHeaders = useBuildPreferencesStore((state) => state.expandedStates || []);
  const expandedStepIds = useBuildPageStore((state) => state.expandedStepIds);
  const expandStep = useBuildPageStore((state) => state.expandStep);
  const filterStateOverrides = useBuildPageStore((state) => state.filterStateOverrides);
  const { currentStep, currentJob, currentLatestJobUuid } = useCurrentJobOrStep();

  /**
   * Expand the current step/job (and its group) when the selection changes, so
   * the selected node's children are revealed — jobs for a container step, or
   * retry history for a command step / parallel / matrix job.
   */
  useEffect(() => {
    if (currentJob) {
      expandStep(currentJob.stepUuid);
      // The job's own node, so selecting a parallel/matrix job opens its
      // retry history (keyed by job id, not the parent step uuid).
      expandStep(currentJob.id);

      if (currentLatestJobUuid && currentLatestJobUuid !== currentJob.id) {
        expandStep(currentLatestJobUuid);
      }
    }

    if (currentJob?.groupUuid) {
      expandStep(currentJob.groupUuid);
    }

    if (currentStep) {
      expandStep(currentStep.uuid);
    }

    if (currentStep?.groupUuid) {
      expandStep(currentStep?.groupUuid);
    }
  }, [currentJob?.id, currentLatestJobUuid, currentStep?.uuid]);

  /**
   * Find steps awaiting input
   *
   * TODO: Update to only check `waiting_for_input` once feature has been released.
   */
  const stepsAwaitingInput = useMemo(() => {
    return steps.filter((step) => {
      return (
        step.state === State.WaitingForInput ||
        (step.type === "input" && step.state !== State.Finished && step.outcome !== Outcome.Errored)
      );
    });
  }, [steps]);

  /**
   * Expand groups with children awaiting input (unless it's already been collapsed)
   *
   * TODO: Update to only expand if the group step is `waiting_for_input` once feature has been released.
   */
  useEffect(() => {
    stepsAwaitingInput.map((step) => {
      if (
        step.type === "group" &&
        step.state === State.WaitingForInput &&
        !expandedStepIds.has(step.uuid)
      ) {
        expandStep(step.uuid);
      } else if (step.groupUuid && !expandedStepIds.has(step.groupUuid)) {
        expandStep(step.groupUuid);
      }
    });
  }, [stepsAwaitingInput]);

  /**
   * Find steps with failures.
   *
   * Default command steps are excluded: auto-expanding them only reveals retry
   * attempts, which should stay collapsed until the user opens them. Steps with
   * job children (groups, parallel, matrix) still auto-expand to surface
   * failures.
   */
  const stepsWithFailures = useMemo(() => {
    return filterSteps(steps, [FilterByOption.Failed], build).filter(
      (step) => !isDefaultCommandStep(step),
    );
  }, [steps]);

  /**
   * Expand all steps with failures (unless it's already been collapsed)
   */
  useEffect(() => {
    stepsWithFailures.map((step) => !expandedStepIds.has(step.uuid) && expandStep(step.uuid));
  }, [stepsWithFailures]);

  /**
   * Derive the effective collapsible state map synchronously.
   *
   * State headers are resolved from three sources (highest priority first):
   *   1. Filter-session user override (user toggled during active filter)
   *   2. Filter auto-expand (filter matches this state)
   *   3. localStorage preference
   *
   * All other nodes (steps, groups) use expandedStepIds directly.
   */
  const currentFilterKey = filterKey(filters);

  return useMemo(() => {
    const map = new Map<string, CollapsibleState>();

    // Auto-expand the current selection and input-awaiting steps. Failures
    // auto-expand only for steps with job children (see stepsWithFailures), so
    // a failed command step's retry history stays collapsed until selected.
    if (currentStep?.uuid) {
      map.set(currentStep.uuid, CollapsibleState.Expanded);
    }
    if (currentStep?.groupUuid) {
      map.set(currentStep.groupUuid, CollapsibleState.Expanded);
    }

    for (const step of stepsWithFailures) {
      map.set(step.uuid, CollapsibleState.Expanded);
    }
    for (const step of stepsAwaitingInput) {
      map.set(step.uuid, CollapsibleState.Expanded);
    }

    // Apply ephemeral step/group overrides (excluding state header IDs)
    for (const [id, state] of expandedStepIds) {
      if (!StateOrder.includes(id as FilterByOption)) {
        map.set(id, state);
      }
    }

    // Resolve state headers
    for (const state of StateOrder) {
      const isFilterMatched = filters.includes(state);
      const override = filterStateOverrides.get(state);

      if (isFilterMatched) {
        // Check for a user override scoped to the current filter session
        if (override && override.filterKey === currentFilterKey) {
          map.set(state, override.state);
        } else {
          map.set(state, CollapsibleState.Expanded);
        }
      } else if (expandedStateHeaders.includes(state)) {
        map.set(state, CollapsibleState.Expanded);
      } else {
        map.set(state, CollapsibleState.Collapsed);
      }
    }

    return map;
  }, [
    currentStep,
    stepsWithFailures,
    stepsAwaitingInput,
    expandedStepIds,
    filters,
    currentFilterKey,
    filterStateOverrides,
    expandedStateHeaders,
  ]);
}
